import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Loader2, ImageIcon, Palette } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Renders the Edpies logo SVG inline (also used to drive the PNG export).
 * Two flavours: with the purple rounded-square background, or transparent
 * (the bars / dot rendered in the brand purple instead of white).
 */
const LogoMark = ({ size = 200, showBackground = true }) => {
  const fg = showBackground ? '#ffffff' : '#7C3AED';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {showBackground && <rect x="0" y="0" width="100" height="100" rx="22" fill="#7C3AED" />}
      <rect x="18" y="20" rx="5" width="45" height="12" fill={fg} />
      <rect x="18" y="44" rx="5" width="35" height="12" fill={fg} />
      <rect x="18" y="68" rx="5" width="45" height="12" fill={fg} />
      <circle cx="78" cy="74" r="7" fill={fg} />
    </svg>
  );
};

/** Build an SVG string suitable for download / canvas rendering. */
const buildSvgString = ({ showBackground }) => {
  const fg = showBackground ? '#ffffff' : '#7C3AED';
  const bgRect = showBackground
    ? '<rect x="0" y="0" width="100" height="100" rx="22" fill="#7C3AED"/>'
    : '';
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="1024" height="1024" viewBox="0 0 100 100" fill="none">
  ${bgRect}
  <rect x="18" y="20" rx="5" width="45" height="12" fill="${fg}"/>
  <rect x="18" y="44" rx="5" width="35" height="12" fill="${fg}"/>
  <rect x="18" y="68" rx="5" width="45" height="12" fill="${fg}"/>
  <circle cx="78" cy="74" r="7" fill="${fg}"/>
</svg>`;
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * Rasterise the SVG into a PNG at the requested pixel size using a canvas.
 * Returns a Promise<Blob>.
 */
const svgToPngBlob = (svgString, sizePx) =>
  new Promise((resolve, reject) => {
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = sizePx;
        canvas.height = sizePx;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, sizePx, sizePx);
        ctx.drawImage(img, 0, 0, sizePx, sizePx);
        URL.revokeObjectURL(url);
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('canvas.toBlob returned null'));
        }, 'image/png');
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });

const DownloadBtn = ({ children, busyKey, busy, ...props }) => (
  <Button
    variant="outline"
    size="sm"
    disabled={busy !== null}
    {...props}
  >
    {busy === busyKey ? (
      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
    ) : (
      <Download className="h-4 w-4 mr-1.5" />
    )}
    {children}
  </Button>
);

const BrandAssetsCard = () => {
  const [busy, setBusy] = useState(null);

  const handleSvgDownload = (showBackground) => {
    try {
      const svg = buildSvgString({ showBackground });
      const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
      triggerDownload(blob, `edpies-logo${showBackground ? '' : '-transparent'}.svg`);
      toast.success('SVG download started');
    } catch (e) {
      toast.error('Failed to prepare SVG');
    }
  };

  const handlePngDownload = async (size, showBackground) => {
    const key = `png-${size}-${showBackground ? 'bg' : 'tx'}`;
    setBusy(key);
    try {
      const svg = buildSvgString({ showBackground });
      const blob = await svgToPngBlob(svg, size);
      triggerDownload(blob, `edpies-logo-${size}px${showBackground ? '' : '-transparent'}.png`);
      toast.success(`${size}×${size} PNG downloaded`);
    } catch (e) {
      toast.error('Failed to generate PNG');
    } finally {
      setBusy(null);
    }
  };

  const handleCopyColor = async () => {
    try {
      await navigator.clipboard.writeText('#7C3AED');
      toast.success('Brand colour #7C3AED copied');
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  return (
    <Card className="shadow-xl mb-8" data-testid="admin-brand-assets-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base md:text-lg">
          <ImageIcon className="h-5 w-5 text-purple-600" />
          Edpies Brand Assets
        </CardTitle>
        <CardDescription className="text-xs md:text-sm">
          Download the Edpies logo for marketing, social media, partner decks or print. SVG is vector
          (best for print and resizing). PNG is rasterised at the size you choose.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid md:grid-cols-2 gap-6">
        {/* With purple background variant */}
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-800">Primary (purple background)</p>
            <span className="text-[10px] uppercase tracking-wide text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">Default</span>
          </div>
          <div className="flex items-center justify-center bg-slate-100 rounded-xl py-6">
            <LogoMark size={140} showBackground={true} />
          </div>
          <div className="flex flex-wrap gap-2">
            <DownloadBtn
              busy={busy}
              onClick={() => handleSvgDownload(true)}
              data-testid="download-svg-bg-btn"
            >
              SVG
            </DownloadBtn>
            <DownloadBtn
              busy={busy}
              busyKey="png-256-bg"
              onClick={() => handlePngDownload(256, true)}
              data-testid="download-png-256-bg-btn"
            >
              PNG 256
            </DownloadBtn>
            <DownloadBtn
              busy={busy}
              busyKey="png-512-bg"
              onClick={() => handlePngDownload(512, true)}
              data-testid="download-png-512-bg-btn"
            >
              PNG 512
            </DownloadBtn>
            <DownloadBtn
              busy={busy}
              busyKey="png-1024-bg"
              onClick={() => handlePngDownload(1024, true)}
              data-testid="download-png-1024-bg-btn"
            >
              PNG 1024
            </DownloadBtn>
          </div>
        </div>

        {/* Transparent variant */}
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-800">Mark only (transparent)</p>
            <span className="text-[10px] uppercase tracking-wide text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full">For light backgrounds</span>
          </div>
          <div
            className="flex items-center justify-center rounded-xl py-6"
            style={{
              backgroundImage:
                'linear-gradient(45deg, #f1f5f9 25%, transparent 25%), linear-gradient(-45deg, #f1f5f9 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f1f5f9 75%), linear-gradient(-45deg, transparent 75%, #f1f5f9 75%)',
              backgroundSize: '16px 16px',
              backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
              backgroundColor: '#ffffff',
            }}
          >
            <LogoMark size={140} showBackground={false} />
          </div>
          <div className="flex flex-wrap gap-2">
            <DownloadBtn
              busy={busy}
              onClick={() => handleSvgDownload(false)}
              data-testid="download-svg-tx-btn"
            >
              SVG
            </DownloadBtn>
            <DownloadBtn
              busy={busy}
              busyKey="png-256-tx"
              onClick={() => handlePngDownload(256, false)}
              data-testid="download-png-256-tx-btn"
            >
              PNG 256
            </DownloadBtn>
            <DownloadBtn
              busy={busy}
              busyKey="png-512-tx"
              onClick={() => handlePngDownload(512, false)}
              data-testid="download-png-512-tx-btn"
            >
              PNG 512
            </DownloadBtn>
            <DownloadBtn
              busy={busy}
              busyKey="png-1024-tx"
              onClick={() => handlePngDownload(1024, false)}
              data-testid="download-png-1024-tx-btn"
            >
              PNG 1024
            </DownloadBtn>
          </div>
        </div>

        {/* Brand colour swatch */}
        <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl shadow-inner" style={{ backgroundColor: '#7C3AED' }} />
            <div>
              <p className="text-sm font-semibold text-slate-800">Brand Purple</p>
              <p className="text-xs text-slate-500">Hex <span className="font-mono">#7C3AED</span> · RGB 124, 58, 237</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopyColor}
            className="text-purple-700 hover:text-purple-900"
            data-testid="copy-brand-colour-btn"
          >
            <Palette className="h-4 w-4 mr-1.5" />
            Copy hex
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default BrandAssetsCard;
